import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import "./EmployeeDetail.scss";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Empty, Tabs, Tag, Timeline, Table, Tooltip } from "antd";
import { useEmployee } from "Hooks/AdminHooks/Organization/useEmployee";
import PropTypes from "prop-types";
import {
  AimOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  CheckOutlined,
  CodeOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FolderOpenOutlined,
  GlobalOutlined,
  IdcardOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import EmployeeDocumentFolderView from "Pages/AdministratorDashboard/DocumentsStore/EmployeeDocumentFolderView";
import PayBonusW2Panel from "Pages/EmployeeDashboard/MyPayBonus/PayBonusW2Panel";

const fmtReviewDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "N/A";

// Columns for the HR Performance Reviews tab. Reuses the existing
// PerformanceEvaluation/Employee/Performance-Reviews response shape.
const performanceReviewColumns = [
  {
    title: "Review Period",
    key: "period",
    render: (_, r) => `${fmtReviewDate(r.periodStart)} - ${fmtReviewDate(r.periodEnd)}`,
  },
  {
    title: "Score",
    dataIndex: "totalPerformanceScore",
    key: "totalPerformanceScore",
    render: (s) => (s !== undefined && s !== null ? `${Number(s).toFixed(1)}/100` : "N/A"),
  },
  {
    title: "Rating",
    dataIndex: "performanceRatingName",
    key: "performanceRatingName",
    render: (v, r) => <Tag>{v || r.performanceRatingCode || "N/A"}</Tag>,
  },
  {
    title: "Reviewed By",
    dataIndex: "reviewerName",
    key: "reviewerName",
    render: (v) => v || "N/A",
  },
  {
    title: "Reviewed On",
    dataIndex: "createdOn",
    key: "createdOn",
    render: (v) => fmtReviewDate(v),
  },
];

const CompensationCard = ({ compensation }) => {
  return (
    <Card bordered={false} className="shadow-sm " style={{ borderRadius: "12px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">Compensation Details</h5>
        <Tag color={compensation.isActive ? "success" : "default"}>
          {compensation.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      <div className="compensation-amount mb-3">
        <span className="amount-currency">{compensation.currency}</span>
        <span className="amount-value">{compensation.baseAmount.toLocaleString()}</span>
        <span className="amount-period">{compensation.payType === 0 ? "/Annual" : "/Monthly"}</span>
      </div>

      <div className="info-grid">
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Compensation ID :
          </strong>
          <span className="info-value">{compensation.publicId}</span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Pay Type :
          </strong>
          <span className="info-value">{compensation.payType === 0 ? "Annual" : "Monthly"}</span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Effective Period :
          </strong>
          <span className="info-value">
            {new Date(compensation.effectiveFrom).toLocaleDateString()} →{" "}
            {compensation.effectiveTo
              ? new Date(compensation.effectiveTo).toLocaleDateString()
              : "Present"}
          </span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Created On :
          </strong>
          <span className="info-value">
            {new Date(compensation.createdOn).toLocaleDateString()}
          </span>
        </div>
      </div>
    </Card>
  );
};

CompensationCard.propTypes = {
  compensation: PropTypes.shape({
    publicId: PropTypes.string.isRequired,
    baseAmount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    payType: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string.isRequired,
    effectiveTo: PropTypes.string, // This can be null, so it's not required
    isActive: PropTypes.bool.isRequired,
    createdOn: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
};

const EmploymentCard = ({ employment }) => {
  const from = new Date(employment.effectiveFrom);
  const to = employment.effectiveTo ? new Date(employment.effectiveTo) : null;

  const probationStart = new Date(employment.probationStartDate);
  const probationEnd = new Date(employment.probationEndDate);

  const isProbation = from >= probationStart && from <= probationEnd;

  return (
    <Card bordered={false} className="shadow-sm" style={{ borderRadius: "12px" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="fw-bold mb-0">{employment.jobTitleName}</h5>

        <div>
          {employment.isActive && <Tag color="green">Active</Tag>}

          {isProbation && <Tag color="orange">Probation</Tag>}
        </div>
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Department :</strong> {employment.departmentName}
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Designation :</strong> {employment.designationName}
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Supervisor :</strong> {employment.supervisorName}
      </div>

      <div className="text-muted">
        {from.toLocaleDateString()} → {to ? to.toLocaleDateString() : "Present"}
      </div>
    </Card>
  );
};

EmploymentCard.propTypes = {
  employment: PropTypes.shape({
    employeeId: PropTypes.number.isRequired,
    supervisorId: PropTypes.number,
    departmentId: PropTypes.number,
    jobTitleId: PropTypes.number,
    designationId: PropTypes.number,

    effectiveFrom: PropTypes.string.isRequired,
    effectiveTo: PropTypes.string,

    probationStartDate: PropTypes.string,
    probationEndDate: PropTypes.string,
    actualProbationEndDate: PropTypes.string,

    isActive: PropTypes.bool.isRequired,

    supervisorName: PropTypes.string,
    departmentName: PropTypes.string,
    jobTitleName: PropTypes.string,
    designationName: PropTypes.string,
  }).isRequired,
};

const CareerPathTab = ({
  data,
  loading,
  isApplicable = true,
  notApplicableMessage = "",
}) => {
  const navigate = useNavigate();
  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  // Executive-employee scenario per Mudassir May-20 doc: API returns
  // { isApplicable: false, message }. Show a clean empty state instead of
  // the default "no career path defined" so HR understands WHY there's no
  // career path for this employee.
  if (!isApplicable) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "60px 24px",
          maxWidth: 520,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "radial-gradient(circle at center, #dbeafe 0%, #eff6ff 80%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.8rem",
            marginBottom: 18,
          }}
        >
          🌟
        </div>
        <h4 style={{ margin: 0, color: "#0f5a2a", fontWeight: 700 }}>
          Career Path is not applicable
        </h4>
        <p
          style={{
            fontSize: "0.95rem",
            color: "#5b6f8c",
            lineHeight: 1.55,
            marginTop: 10,
            maxWidth: 460,
          }}
        >
          {notApplicableMessage ||
            "This employee holds an executive role that spans the organization. The standard career-path progression doesn't apply to org-wide leadership positions."}
        </p>
      </div>
    );
  }
  if (!data?.currentPath) return <Empty description="No career path defined" />;

  const { currentPath, employee, hasAlternativeOptions } = data;

  return (
    <div className="modern-journey-container p-4">
      {/* 1. Top Experience Row - Shorter & Stylish */}
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="d-flex align-items-center justify-content-between p-3 rounded-4"
            style={{
              background: "linear-gradient(90deg, #f6ffed 0%, #ffffff 100%)",
              borderLeft: "5px solid #52c41a",
              boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
            }}
          >
            {/* Title Section */}
            <div>
              <div className="d-flex align-items-center gap-3">
                <h4 className="fw-bold mb-0">Career Progression Roadmap</h4>
                <Tooltip
                  title={
                    hasAlternativeOptions
                      ? ""
                      : "No alternative paths for this employee. They appear only when the department has more than one career path configured."
                  }
                >
                  <span
                    style={{
                      cursor: hasAlternativeOptions ? "default" : "not-allowed",
                    }}
                  >
                    <Button
                      icon={<ApartmentOutlined />}
                      className={hasAlternativeOptions ? "btn-primary" : ""}
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        ...(hasAlternativeOptions ? {} : { pointerEvents: "none" }),
                      }}
                      disabled={!hasAlternativeOptions}
                      onClick={() =>
                        navigate(`/alternative-careerPaths/${employee.employeeId}`)
                      }
                    >
                      Alternative Paths
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <div className="d-flex align-items-center mt-1">
                <CalendarOutlined className="text-muted me-1" style={{ fontSize: "12px" }} />
                <span className="text-muted small">
                  Joined on{" "}
                  <strong>
                    {employee?.hiredDate
                      ? new Date(employee.hiredDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "N/A"}
                  </strong>
                </span>
              </div>
            </div>

            {/* Experience Stats Group */}
            <div className="d-flex gap-4 align-items-center">
              {/* Prior Experience */}
              <div className="text-end border-end pe-4">
                <span
                  className="text-muted fw-bold small text-uppercase d-block"
                  style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                >
                  Prior Experience
                </span>
                <div className="h4 fw-bold mb-0" style={{ color: "#8c8c8c" }}>
                  {(employee?.priorExperienceYears || 0).toFixed(1)}{" "}
                  <small className="h6">Yrs</small>
                </div>
              </div>

              {/* Total Experience Highlight */}
              <div className="text-end">
                <span
                  className="text-success fw-bold small text-uppercase d-block"
                  style={{ fontSize: "11px", letterSpacing: "1px" }}
                >
                  Total Experience
                </span>
                <div className="display-6 fw-bold text-success mb-0" style={{ lineHeight: 1 }}>
                  {(employee?.totalExperienceYears || 0).toFixed(1)}{" "}
                  <small className="h4">Yrs</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Skills & Domains - Side by Side */}
      <div className="row g-4 mb-5">
        <div className="col-md-6 border-end">
          <span
            className="text-muted small fw-bold text-uppercase d-block mb-3"
            style={{ letterSpacing: "0.5px" }}
          >
            <CodeOutlined className="me-2" /> Technical Skills of Employee
          </span>
          <div className="d-flex flex-wrap gap-2">
            {employee?.employeeSkills?.map((skill, index) => (
              <Tag key={index} className="skill-pill-premium">
                {skill.skillName}
              </Tag>
            ))}
          </div>
        </div>

        <div className="col-md-6 ps-md-4">
          <span
            className="text-muted small fw-bold text-uppercase d-block mb-3"
            style={{ letterSpacing: "0.5px" }}
          >
            <GlobalOutlined className="me-2" /> Domain Expertise of Employee
          </span>
          <div className="d-flex flex-wrap gap-2">
            {employee?.domainKnowledges?.map((domain, index) => (
              <Tag
                key={index}
                color="gold"
                className="border-0 rounded-pill px-3 py-1"
                style={{ fontSize: "11px", fontWeight: 600 }}
              >
                {domain.name}
              </Tag>
            ))}
          </div>
        </div>
      </div>

      {/* 3. The Journey Track */}
      <div className="journey-track-wrapper custom-scrollbar">
        <div className="journey-line"></div>
        {/* ================= PATH CONTEXT ================= */}
        {/* PATH CONTEXT - DOMAIN KNOWLEDGE PANEL */}
        <div className="path-context-overlay mb-4" style={{ marginTop: "20px" }}>
          {/* PATH IDENTITY INFO */}
          <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
            <div>
              <div className="d-flex align-items-center gap-2">
                <span
                  className="badge bg-primary-subtle text-primary border border-primary-subtle"
                  style={{ fontSize: "10px" }}
                >
                  {currentPath.publicId}
                </span>
                <h5 className="mb-0 fw-bold">{currentPath.name} Path</h5>
              </div>
              <p className="text-muted small mb-0 mt-1" style={{ fontStyle: "italic" }}>
                {currentPath.description}
              </p>
            </div>
          </div>

          <div className="context-header d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <GlobalOutlined className="context-icon" />
              <span className="context-title">Domain Knowledge Context</span>
            </div>

            <span className="context-badge">
              {currentPath.domainKnowledges?.length || 0} Domains
            </span>
          </div>

          <div className="context-tags">
            {currentPath.domainKnowledges?.map((domain) => (
              <div key={domain.domainKnowledgeId} className="domain-glass-card">
                <div className="domain-name">{domain.name}</div>
                <div className="domain-industry">{domain.industry}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="milestones-container">
          {currentPath.jobTitles?.map((step, index) => {
            const isCurrent = step.isCurrent;
            const isCompleted = step.skillMatchPercentage === 100;
            const isFuture = step.isFuture;
            const expMet = step.meetsExperienceRequirement;

            return (
              <div
                key={step.id}
                className={`milestone-node 
          ${isCurrent ? "active" : ""} 
          ${isCompleted ? "completed" : ""} 
          ${isFuture ? "future" : ""}`}
              >
                {/* ================= MARKER ================= */}
                <div className="marker-wrapper">
                  <div className="marker-dot">{isCompleted ? <CheckOutlined /> : index + 1}</div>
                  {isCurrent && <div className="marker-pulse"></div>}
                </div>

                {/* ================= CARD ================= */}
                <div className="milestone-content">
                  <div className="milestone-card border-0 shadow-sm">
                    {/* HEADER */}
                    <div className="card-top d-flex justify-content-between align-items-start">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <span className="level-tag">LEVEL {index + 1}</span>

                          {/* EMPLOYEE COUNT KPI */}
                          <span className="employee-chip">👥 {step.employeeCount} Employees</span>

                          {/* STATUS BADGES */}
                          {isCurrent && <Tag color="green">CURRENT</Tag>}
                          {isFuture && <Tag color="blue">FUTURE</Tag>}
                          {isCompleted && <Tag color="gold">MASTERED</Tag>}
                        </div>

                        <h6 className="role-title mb-1">{step.name}</h6>

                        <p className="tiny-desc text-muted mb-0">{step.description}</p>
                      </div>
                    </div>

                    {/* ================= SKILLS ================= */}
                    <div className="skill-preview mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted fw-bold">SKILL MATCH</small>
                        <span className="tiny-percent">
                          {Math.round(step.skillMatchPercentage)}%
                        </span>
                      </div>

                      <div className="skills-mini-list">
                        {step.requiredSkills?.map((skill) => (
                          <div key={skill.skillId} className="mini-skill-row">
                            <span
                              className={skill.hasSkill ? "skill-text-active" : "skill-text-muted"}
                            >
                              {skill.skillName}
                            </span>

                            {skill.hasSkill ? (
                              <span className="skill-ok">✓</span>
                            ) : (
                              <div className="empty-dot" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ================= EXPERIENCE ================= */}
                    <div className="card-footer-exp mt-3">
                      <div
                        className={`exp-badge w-100 justify-content-center 
                  ${expMet ? "exp-ok" : "exp-missing"}`}
                      >
                        🎯 {step.minExperienceRequired}
                        {step.maxExperienceRequired ? ` - ${step.maxExperienceRequired}` : "+"}{" "}
                        Years Required
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CareerPathTab.propTypes = {
  loading: PropTypes.bool,
  isApplicable: PropTypes.bool,
  notApplicableMessage: PropTypes.string,
  data: PropTypes.shape({
    employee: PropTypes.object,
    hasAlternativeOptions: PropTypes.bool,
    currentPath: PropTypes.shape({
      jobTitles: PropTypes.array,
      domainKnowledges: PropTypes.array,
      name: PropTypes.string,
      description: PropTypes.string,
      publicId: PropTypes.string,
    }),
  }),
};

function EmployeeDetail() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("1"); // default tab
  const { empId } = useParams();
  const navigate = useNavigate();
  const {
    fetchEmployeeDetail,
    employeeDetail,
    loadingDetail,

    compensations,
    loadingCompensation,
    fetchCompensations,
    compensationsLoaded,

    employments,
    loadingEemployments,
    employmentsLoaded,
    fetchEmployments,

    directReports,
    loadingDirectReports,
    directReportsLoaded,
    fetchDirectReports,

    careerPaths,
    loadingCareerPaths,
    careerPathsLoaded,
    fetchCareerPaths,
    careerPathIsApplicable,
    careerPathNotApplicableMessage,

    columns,
    performanceReviews,
    loadingPerformanceReviews,
    performanceReviewsLoaded,
    fetchPerformanceReviews,
    resetEmployeeTabs,
  } = useEmployee();

 useEffect(() => {
  if (location.state?.activeTab) {
    setActiveTab(location.state.activeTab);
  }

  if (location.state?.refresh) {
    fetchEmployeeDetail(empId);      
    fetchEmployments(empId);            
  }
}, [location.state]);

  useEffect(() => {
    if (empId) {
      resetEmployeeTabs();
      fetchEmployeeDetail(empId);
    }
  }, [empId]);

  // StatCard component
  const StatCard = ({ title, value, publicId, icon: Icon, color }) => {
    const colorMap = {
      primary: "#599c16",
      success: "#206d1a",
      info: "#14240e",
      warning: "#f59e0b",
      danger: "#ef4444",
    };

    const activeColor = colorMap[color] || colorMap.primary;

    return (
      <Card
        bordered={false}
        className="shadow-sm stat-card"
        style={{ borderRadius: "10px" }}
        bodyStyle={{ padding: "12px 14px" }}
      >
        <div className="d-flex justify-content-between align-items-center h-100">
          <div className="stat-content">
            <div className="stat-title">{title}</div>

            <div className="stat-value" title={value}>
              {value}
            </div>

            {publicId && <div className="stat-id">{publicId}</div>}
          </div>

          {Icon && <Icon style={{ fontSize: "1.6rem", color: activeColor }} />}
        </div>
      </Card>
    );
  };

  StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    publicId: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    color: PropTypes.string,
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "2" && !compensationsLoaded) {
      fetchCompensations(empId);
    }

    if (key === "3" && !employmentsLoaded) {
      fetchEmployments(empId);
    }

    if (key === "4" && !directReportsLoaded) {
      fetchDirectReports(empId);
    }

    if (key === "5" && !careerPathsLoaded) {
      fetchCareerPaths(empId);
    }

    if (key === "6" && !performanceReviewsLoaded) {
      fetchPerformanceReviews(empId);
    }
  };

  if (loadingDetail || !employeeDetail) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: 24 }}>Loading Employee Detail...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "24px" }}>
        {/*  Header */}
        <div
          className="employee-header"
          style={{
            background: "linear-gradient(to right, #f8f9fa, #ffffff)",
            padding: "24px 32px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-2">
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "#e9ecef",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#495057",
                  }}
                >
                  {employeeDetail.fullName ? employeeDetail.fullName.charAt(0).toUpperCase() : "E"}
                </div>
                <div>
                  <h1
                    className="mb-1"
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#212529",
                      margin: 0,
                    }}
                  >
                    {employeeDetail.fullName || "Employee Name"}
                  </h1>
                  <div className="d-flex align-items-center mt-1">
                    <Tag color="green" style={{ fontSize: "12px", padding: "2px 8px" }}>
                      {employeeDetail.designationName || "Designation"}
                    </Tag>
                    <span className="text-muted ms-2" style={{ fontSize: "14px" }}>
                      ID: #{employeeDetail.publicId || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                className="btn-back"
                icon={<i className="fas fa-arrow-left"></i>}
                onClick={() => navigate("/organization/directory")}
              >
                Back
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="row g-3 mb-4">
          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <StatCard
              title="Current Department"
              value={employeeDetail?.department?.name || "Org-Wide Role"}
              publicId={employeeDetail?.department?.publicId || ""}
              icon={ApartmentOutlined}
              color="primary"
            />
          </div>

          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <StatCard
              title="Current Job Title"
              value={employeeDetail?.jobTitle?.name || "N/A"}
              publicId={employeeDetail?.jobTitle?.publicId || ""}
              icon={IdcardOutlined}
              color="info"
            />
          </div>

          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <StatCard
              title="Current Designation"
              value={employeeDetail?.designation?.name || "N/A"}
              publicId={employeeDetail?.designation?.publicId || ""}
              icon={SolutionOutlined}
              color="warning"
            />
          </div>

          <div className="col-xl-3 col-lg-6 col-md-6 col-12">
            <StatCard
              title="Current Supervisor"
              value={employeeDetail?.supervisor?.fullName || "N/A"}
              publicId={employeeDetail?.supervisor?.publicId || ""}
              icon={TeamOutlined}
              color="success"
            />
          </div>
        </div>

        {/* Tabs Area */}
        <Tabs activeKey={activeTab} onChange={handleTabChange} className="custom-tabs">
          <Tabs.TabPane
            tab={
              <span>
                <ApartmentOutlined />
                Overview
              </span>
            }
            key="1"
          >
            <div className="employee-overview">
              <div className="row g-4">
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-user me-2"></i>Personal Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Full Name :
                      </span>
                      <span className="info-value ">{employeeDetail.fullName || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Email :
                      </span>
                      <span className="info-value">{employeeDetail.workEmail || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Phone :
                      </span>
                      <span className="info-value">{employeeDetail.mobileno || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Gender :
                      </span>
                      <span className="info-value">{employeeDetail.gender || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Marital Status :
                      </span>
                      <span className="info-value">{employeeDetail.maritalStatus || "N/A"}</span>
                    </div>
                  </Card>
                </div>

                {/* Employment Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-briefcase me-2"></i>Employment Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Employee ID :
                      </span>
                      <span className="info-value">{employeeDetail.publicId || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Department :
                      </span>
                      <span className="info-value">{employeeDetail.department?.name || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Job Title :
                      </span>
                      <span className="info-value">{employeeDetail.jobTitle?.name || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Designation :
                      </span>
                      <span className="info-value">
                        {employeeDetail.designation?.name || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Hire Date :
                      </span>
                      <span className="info-value">
                        {employeeDetail.hiredDate
                          ? new Date(employeeDetail.hiredDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </Card>
                </div>

                {/* Address Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-map-marker-alt me-2"></i>Address Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Street Address :
                      </span>
                      <span className="info-value">{employeeDetail.streetAddress || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        City :
                      </span>
                      <span className="info-value">{employeeDetail.city || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        State :
                      </span>
                      <span className="info-value">{employeeDetail.state || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Postal Code :
                      </span>
                      <span className="info-value">{employeeDetail.postalCode || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Country :
                      </span>
                      <span className="info-value">{employeeDetail.country || "N/A"}</span>
                    </div>
                  </Card>
                </div>

                {/* Supervisor & Probation Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-user-tie me-2"></i>Supervisor & Probation
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Supervisor :
                      </span>
                      <span className="info-value">
                        {employeeDetail.supervisor?.fullName || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Supervisor Email :
                      </span>
                      <span className="info-value">
                        {employeeDetail.supervisor?.workEmail || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Probation Start :
                      </span>
                      <span className="info-value">
                        {employeeDetail.probationStartDate
                          ? new Date(employeeDetail.probationStartDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Probation End :
                      </span>
                      <span className="info-value">
                        {employeeDetail.probationEndDate
                          ? new Date(employeeDetail.probationEndDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Actual Probation End :
                      </span>
                      <span className="info-value">
                        {employeeDetail.actualProbationEndDate
                          ? new Date(employeeDetail.actualProbationEndDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </Card>
                </div>
              </div>
              {/* 1. Skills & Domains (Full Width at Top) */}
              <div className="col-12" style={{ marginTop: "20px" }}>
                <Card
                  title={
                    <>
                      <i className="fas fa-lightbulb me-2"></i>Skills & Expertise
                    </>
                  }
                  className="card-professional"
                  headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                >
                  <div className="row g-4">
                    {/* Technical Skills Column */}
                    <div className="col-md-6 border-end">
                      <span className="text-muted small fw-bold text-uppercase d-block mb-2">
                        Technical Skills
                      </span>
                      <div className="d-flex flex-wrap gap-2">
                        {employeeDetail.employeeSkills?.length > 0 ? (
                          employeeDetail.employeeSkills.map((skill, index) => (
                            <Tag
                              key={index}
                              color="blue"
                              className="rounded-pill px-3 py-1 border-0"
                            >
                              {skill.skillName}
                            </Tag>
                          ))
                        ) : (
                          <span className="text-muted italic">No skills listed</span>
                        )}
                      </div>
                    </div>

                    {/* Domain Knowledge Column */}
                    <div className="col-md-6 ps-md-4">
                      <span className="text-muted small fw-bold text-uppercase d-block mb-2">
                        Domain Knowledge
                      </span>
                      <div className="d-flex flex-wrap gap-2">
                        {employeeDetail.domainKnowledges?.length > 0 ? (
                          employeeDetail.domainKnowledges.map((domain, index) => (
                            <Tag
                              key={index}
                              color="cyan"
                              className="rounded-pill px-3 py-1 border-0"
                            >
                              {domain.name}{" "}
                              <small style={{ opacity: 0.8 }}>({domain.industry})</small>
                            </Tag>
                          ))
                        ) : (
                          <span className="text-muted italic">No domain knowledge listed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </Tabs.TabPane>

          {/* Compensation Tab */}
          <Tabs.TabPane
            tab={
              <span>
                <SolutionOutlined /> Compensation
              </span>
            }
            key="2"
          >
            <div className="timeline-wrapper">
              {loadingCompensation ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="mt-2">Loading compensation details...</p>
                </div>
              ) : compensations && compensations.length > 0 ? (
                <Timeline mode="left" className="custom-timeline">
                  {compensations.map((compensation) => {
                    const fromDate = new Date(compensation.effectiveFrom);
                    const toDate = compensation.effectiveTo
                      ? new Date(compensation.effectiveTo)
                      : null;

                    const year = fromDate.getFullYear();

                    return (
                      <Timeline.Item
                        key={compensation.publicId}
                        label={<div className="timeline-year">{year}</div>}
                      >
                        <CompensationCard compensation={compensation} />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <Empty
                  description="No compensation records found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Tabs.TabPane>

          {/* Employment Tab*/}
          <Tabs.TabPane
            tab={
              <span>
                <TeamOutlined /> Employment Timeline
              </span>
            }
            key="3"
          >
            <div className="timeline-wrapper">
              {loadingEemployments ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                  <p>Loading employment history...</p>
                </div>
              ) : employments && employments.length > 0 ? (
                <Timeline mode="left" className="custom-timeline">
                  {employments.map((employment, index) => {
                    const fromDate = new Date(employment.effectiveFrom);
                    const year = fromDate.getFullYear();

                    return (
                      <Timeline.Item
                        key={index}
                        label={<div className="timeline-year">{year}</div>}
                      >
                        <EmploymentCard employment={employment} />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <Empty
                  description="No employment history found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Tabs.TabPane>

          {/* Direct Report Tab*/}
          <Tabs.TabPane
            tab={
              <span>
                <TeamOutlined /> Direct Reports
              </span>
            }
            key="4"
          >
            {loadingDirectReports ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary"></div>
                <p>Loading direct reports...</p>
              </div>
            ) : directReports && directReports.length > 0 ? (
              <Table
                className="custom-table"
                columns={columns}
                dataSource={directReports}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <Empty description="No direct reports found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <AimOutlined /> Career Paths
              </span>
            }
            key="5"
          >
            <CareerPathTab
              data={careerPaths}
              loading={loadingCareerPaths}
              isApplicable={careerPathIsApplicable}
              notApplicableMessage={careerPathNotApplicableMessage}
            />
          </Tabs.TabPane>

          {/* Performance Reviews Tab */}
          <Tabs.TabPane
            tab={
              <span>
                <FileDoneOutlined /> Performance Reviews
              </span>
            }
            key="6"
          >
            <div className="timeline-wrapper">
              {loadingPerformanceReviews ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="mt-2">Loading performance reviews...</p>
                </div>
              ) : performanceReviews && performanceReviews.length > 0 ? (
                <Table
                  rowKey={(r) => r.id}
                  dataSource={performanceReviews}
                  columns={performanceReviewColumns}
                  pagination={{ pageSize: 8 }}
                />
              ) : (
                <Empty
                  description="No performance reviews found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <FolderOpenOutlined /> Documents
              </span>
            }
            key="7"
          >
            <div className="timeline-wrapper">
              <EmployeeDocumentFolderView employeeId={empId} />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <DollarOutlined /> Pay & Bonuses
              </span>
            }
            key="8"
          >
            {activeTab === "8" && <PayBonusW2Panel employeeId={empId} />}
          </Tabs.TabPane>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default EmployeeDetail;
